import React, { ReactElement, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useLocation, Navigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { APIResponse } from '@groma-dev/scaffold';
import { useAppStore, useDataStore } from '../../providers/StoresProvider';
import { AppConfig } from '../../../core/AppConfig';
import { AppRoutes } from '../../../core/routes';
import { FirebaseEvents } from '../../utils/Constants';
import { buildUrlWithParams } from '../../utils/WebHelper';
import { handleErrorLogging } from '../../../core/utils/CoreHelper';

const AuthRouter = observer(({ children }: { children: ReactElement }) => {
  const dataStore = useDataStore();
  const { firebase } = useAppStore();
  const { pathname, search } = useLocation();
  const { userState } = dataStore;
  const { Authentication, Server, Metrics } = AppConfig.Settings;
  const { cookie } = Authentication;
  const { name: authCookieName, domain: authCookieDomain } = cookie;
  const { enabled: protectionEnabled, protectionKey, cookieName: appCookieName } = Server.passwordProtection;
  const authCookiePresent = Cookies.get(authCookieName);
  const appPasswordMatch = Cookies.get(appCookieName) === protectionKey;
  const forceAppProtection = protectionEnabled && !appPasswordMatch;

  const [currentLocation, setCurrentLocation] = useState<string>('');
  const [initialized, setInitialized] = useState<boolean>(false);

  const firePageViewEvent = () => {
    if (Metrics.Firebase.enabled) {
      if (currentLocation !== pathname) {
        setCurrentLocation(pathname);
        firebase.fireEvent(FirebaseEvents.PAGE_VIEW, { path: pathname });
      }
    }
  };

  // App Password Protected
  if (forceAppProtection && pathname !== AppRoutes.Auth) {
    const returnUrl = encodeURIComponent(`${pathname}${search}`);

    return <Navigate to={buildUrlWithParams(`${AppRoutes.Auth}?return_url=${returnUrl}`)} replace />;
  } else if (protectionEnabled && appPasswordMatch && pathname === AppRoutes.Auth) {
    // App requires authentication
    return <Navigate to={buildUrlWithParams(AppRoutes.root)} replace />;
  }

  if (!forceAppProtection && authCookiePresent && !userState.user && !userState.fetchingUser) {
    dataStore
      .fetchUser()
      .then((res: APIResponse) => {
        if (res.error) {
          if (res.status === 401) {
            Cookies.remove(authCookieName, { domain: authCookieDomain });
          }
        }
      })
      .catch(e => handleErrorLogging(e));
  }

  firePageViewEvent();

  if (!initialized) setInitialized(true);

  return children;
});

export default AuthRouter;
